// This file contains the helper functions for the cards in the dashboard

import { useState } from "react";
import down_arrow from "@/assets/down_arrow.svg";

type CardBaseProps = {
	children: React.ReactNode;
	isNew: boolean;
	isOverdue?: boolean;
	hoverChildren?: React.ReactNode;
	canOpen?: boolean;
	className?: string;
	maxHeight?: number;
	hoverText?: string;
	hoverInsteadOfClick?: boolean;
};

/**
 * The base card template that all other cards will use
 * This is used for the Dashboard in the main menu
 *
 * @param children - The children to be rendered inside the card
 * @param isNew - Whether the card is new
 * @param isOverdue - A trigger flag for RFIs and Submittals, changing the border color
 * @param hoverChildren - The buttons to be rendered when the card is hovered over (You'd need to pass the routes on here)
 * @param canOpen - Whether the card can be opened (default is true)
 * @param className - Additional className attributes to be applied to the card
 * @param maxHeight - The maximum height of the card (default is 500px). Increasing this will allow more content to be shown, but too high and the animation will look weird. Mostly used for the TaskOuterContainer component
 * @param hoverText - The text to be displayed when the card is clicked (default is "Actions")
 * @param hoverInsteadOfClick - Whether to hover over the card instead of click to open the card (default is false)
 * @returns A card with the given children and a new tag if isNew is true
 */
export function CardBase({
	children,
	isNew,
	isOverdue = false,
	hoverChildren,
	canOpen = true,
	className,
	maxHeight = 500,
	hoverText = "Actions",
	hoverInsteadOfClick = false,
}: CardBaseProps) {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const border_classes = isOverdue
		? "animate-border-pulse border-red-500 hover:border-red-500"
		: isNew
			? "animate-pulse border-green-200 hover:border-green-200"
			: "border-orange-100 hover:border-orange-200";

	const onClick = (event: React.MouseEvent) => {
		event.stopPropagation(); // If CardBase is nested inside itself or has similar components, this will prevent the event from bubbling up and triggering the same function from its parent
		setIsOpen(!isOpen);
	};

	return (
		<div
			className={`${className} border-2 px-2 py-1 my-2 rounded-md shadow-md transition-all duration-300 dark:border-stone-500
            ${border_classes}
            ${!canOpen ? "flex flex-row justify-between" : ""}
            ${isOpen ? "bg-orange-100 dark:bg-stone-900" : "bg-orange-50 dark:bg-stone-800 hover:bg-orange-100 dark:hover:bg-stone-800/30"}
        `}
			onClick={
				hoverInsteadOfClick ? undefined : onClick
			} // Tbh i don't really like the way this is implemented, but it works for now
			onMouseEnter={hoverInsteadOfClick ? onClick : undefined}
			onMouseLeave={hoverInsteadOfClick ? onClick : undefined}
		>
			{isNew && <h4 className="text-green-500">New</h4>}

			{/* This is used to show info about the card (like Project Name, dates, etc) */}
			{children}

			<OpenSetting
				children={hoverChildren}
				canOpen={canOpen}
				isOpen={isOpen}
				maxHeight={maxHeight}
				hoverText={hoverText}
			/>
		</div>
	);
}

type OpenSettingProps = {
	children: React.ReactNode;
	canOpen: boolean;
	isOpen: boolean;
	maxHeight: number;
	hoverText: string;
};
// Small decider for if the card can open or not
// Kinda stupid implementation but it works
function OpenSetting({
	children,
	canOpen,
	isOpen,
	maxHeight,
	hoverText,
}: OpenSettingProps) {
	if (canOpen) {
		return (
			<>
				<HoverChildren
					children={children}
					isOpen={isOpen}
					maxHeight={maxHeight}
					hoverText={hoverText}
				/>
				<ChildCardBaseOpenButton isOpen={isOpen} />
			</>
		);
	} else {
		return <>{children}</>;
	}
}

function HoverChildren({
	children,
	isOpen,
	maxHeight,
	hoverText,
}: {
	children: React.ReactNode;
	isOpen: boolean;
	maxHeight: number;
	hoverText: string;
}) {
	return (
		<div
			className={`overflow-hidden transition-all duration-200 ease-in-out ${
				isOpen
					? `max-h-[${maxHeight}px] opacity-100 pointer-events-auto`
					: "max-h-0 opacity-0 pointer-events-none"
			}`}
		>
			<div
				className={`transition-all duration-200 transform ${isOpen ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0"}`}
			>
				<p className="my-2 text-center text-gray-500 border-t-2 dark:text-stone-200">
					{hoverText}:
				</p>
				{children}
			</div>
		</div>
	);
}

function ChildCardBaseOpenButton({ isOpen }: { isOpen: boolean }) {
	return (
		<div
			className={`cursor-pointer transition-all duration-200
        ${isOpen ? "rotate-180" : ""}
        `}
		>
			<img
				src={down_arrow}
				alt="down_arrow"
				className="w-3 h-3 mx-auto dark:invert"
			/>
		</div>
	);
}

/**
 * A card that displays a reminder text
 *
 * Used in TaskCard.tsx, RFICard.tsx, and SubmittalCard.tsx
 *
 * @param text - The text to be displayed in the card
 * @returns A card with the given text
 */
export function PingedCardAlert({ text }: { text: string }) {
	return (
		<div className="flex flex-col p-4 rounded-md bg-red-100/50 dark:bg-red-950/40">
			<h4 className="text-red-600">{text}</h4>
		</div>
	);
}

// Color Coded Functions to help visually see the status of important fields
// (Although i don't think this is being used anymore)
export function ColorStatus(status: string) {
	if (status === "Active" || status === "Open" || status === "Paid") {
		return "text-green-500";
	} else if (status === "Closing" || status === "Pending") {
		return "text-yellow-600";
	} else {
		return "text-red-500";
	}
}

export function DaysOldColor(days_old: number) {
	if (days_old < 10) {
		return "text-green-500";
	} else if (days_old < 20) {
		return "text-yellow-700";
	} else {
		return "text-red-500";
	}
}

export function HistoryCardBase({ children }: { children: React.ReactNode }) {
	return (
		<div className="p-2 mx-2 my-4 transition-all duration-300 border-2 rounded-md shadow-md bg-orange-300/5 border-orange-50 hover:bg-orange-100 hover:border-orange-100 dark:bg-stone-900 dark:hover:bg-stone-800 dark:border-stone-500">
			{children}
		</div>
	);
}

export function formatDateTime(date: string) {
	const dateObj = new Date(date);
	return dateObj.toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
		hour: "numeric",
		minute: "numeric",
		second: "numeric",
	});
}

export function formatDate(date: string) {
	const dateObj = new Date(date);
	return dateObj.toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}

export function isRecentlyUpdated(date: string | undefined, hours = 24) {
	if (!date) return false;
	const elapsed = Date.now() - new Date(date).getTime();
	return elapsed >= 0 && elapsed < hours * 60 * 60 * 1000;
}

export function timeAgo(date: string) {
	const elapsedMinutes = Math.floor((Date.now() - new Date(date).getTime()) / 60000);
	if (elapsedMinutes < 1) return "just now";
	if (elapsedMinutes < 60) {
		return `${elapsedMinutes} minute${elapsedMinutes === 1 ? "" : "s"} ago`;
	}
	const elapsedHours = Math.floor(elapsedMinutes / 60);
	return `${elapsedHours} hour${elapsedHours === 1 ? "" : "s"} ago`;
}
